import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Bug, Lightbulb, MessageSquarePlus } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

export type TeacherFeedbackChoice = "bug" | "idea";

interface TeacherFeedbackChoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (choice: TeacherFeedbackChoice) => void;
}

const TeacherFeedbackChoiceDialog: React.FC<TeacherFeedbackChoiceDialogProps> = ({
  open,
  onOpenChange,
  onSelect,
}) => {
  const { t } = useLanguage();

  const handleSelect = (choice: TeacherFeedbackChoice) => {
    onSelect(choice);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus className="h-5 w-5 text-primary" />
            {t("Zpětná vazba", "Spätná väzba")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Co byste nám rádi sdělili?",
              "Čo by ste nám radi oznámili?"
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2 pt-2">
          <button
            type="button"
            onClick={() => handleSelect("bug")}
            className={cn(
              "group flex flex-col items-start gap-2 rounded-lg border border-border/60 bg-background p-4 text-left",
              "transition-all duration-200 hover:border-primary/60 hover:bg-primary/5 hover:shadow-md",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            )}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive transition-colors group-hover:bg-destructive/20">
              <Bug className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold">
                {t("Nahlásit chybu", "Nahlásiť chybu")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t(
                  "Něco nefunguje, jak má.",
                  "Niečo nefunguje, ako má."
                )}
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleSelect("idea")}
            className={cn(
              "group flex flex-col items-start gap-2 rounded-lg border border-border/60 bg-background p-4 text-left",
              "transition-all duration-200 hover:border-primary/60 hover:bg-primary/5 hover:shadow-md",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            )}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 transition-colors group-hover:bg-amber-500/20 dark:text-amber-400">
              <Lightbulb className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold">
                {t("Navrhnout nápad", "Navrhnúť nápad")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t(
                  "Mám nápad na zlepšení aplikace.",
                  "Mám nápad na zlepšenie aplikácie."
                )}
              </p>
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TeacherFeedbackChoiceDialog;
